/**
 * NuGuard AI - Centralized Error Handler
 *
 * Provides consistent error message extraction and categorization across the application.
 * Converts technical errors into user-friendly messages with contextual recovery hints.
 */

import { logger } from '../services/logger';
import { ApiError } from '../services/api';
import type { AIBOMScanResponse } from '../schemas';

/**
 * Categorizes errors by their nature for targeted handling and messaging.
 */
export type ErrorCategory =
  | 'authentication'
  | 'authorization'
  | 'network'
  | 'validation'
  | 'not_found'
  | 'rate_limit'
  | 'server'
  | 'unknown';

/**
 * Structured error result with user-friendly message and metadata.
 */
export interface HandledError {
  message: string;
  category: ErrorCategory;
  originalError: unknown;
  statusCode?: number;
  retryable: boolean;
}

/**
 * Options for configuring error handling behavior.
 */
export interface HandleErrorOptions {
  /** Fallback message if error cannot be categorized */
  fallback?: string;
  /** Additional context for logging (e.g., 'useAIBOM:fetchScans') */
  logContext?: string;
  /** Whether to log the error (default: true) */
  log?: boolean;
}

/**
 * Extract HTTP status code from various error shapes.
 */
function getStatusCode(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null) {
    // FastAPI error response format
    if ('status' in error && typeof error.status === 'number') {
      return error.status;
    }
    // Fetch Response object
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      return error.statusCode;
    }
  }
  return undefined;
}

/**
 * Extract detail message from FastAPI error responses.
 */
function getDetailMessage(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null) {
    if ('detail' in error && typeof error.detail === 'string') {
      return error.detail;
    }
  }
  return undefined;
}

/**
 * Categorize error based on status code and message patterns.
 */
function categorizeError(error: unknown, statusCode?: number): ErrorCategory {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  // Status code categorization (most reliable)
  if (statusCode === 401) return 'authentication';
  if (statusCode === 403) return 'authorization';
  if (statusCode === 404) return 'not_found';
  if (statusCode === 429) return 'rate_limit';
  if (statusCode && statusCode >= 500) return 'server';
  if (statusCode && statusCode >= 400 && statusCode < 500) return 'validation';

  // Message pattern categorization (fallback)
  if (message.includes('unauthorized') || message.includes('401')) return 'authentication';
  if (message.includes('forbidden') || message.includes('403')) return 'authorization';
  if (message.includes('not found') || message.includes('404')) return 'not_found';
  if (message.includes('rate limit') || message.includes('429')) return 'rate_limit';
  if (message.includes('server error') || message.includes('500')) return 'server';
  if (message.includes('network') || message.includes('fetch') || message.includes('connection') || message.includes('timeout') || message.includes('timed out') || message.includes('cancelled') || message.includes('canceled') || message.includes('aborted')) return 'network';
  if (message.includes('validation') || message.includes('invalid') || message.includes('required')) return 'validation';

  return 'unknown';
}

/**
 * Message shown when a request fails due to an expired/invalid session.
 * Exported so callers can recognize this specific case (e.g. to avoid
 * showing a redundant toast while the app is already redirecting to login).
 */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/**
 * Generate user-friendly message based on error category.
 */
function getFriendlyMessage(category: ErrorCategory, detail?: string, fallback?: string): string {
  switch (category) {
    case 'authentication':
      return SESSION_EXPIRED_MESSAGE;
    case 'authorization':
      return 'You do not have permission to perform this action.';
    case 'network':
      return fallback || 'Connection failed. Please check your network and try again.';
    case 'validation':
      return detail || 'Invalid input. Please check your data and try again.';
    case 'not_found':
      return 'The requested resource was not found.';
    case 'rate_limit':
      return 'Too many requests. Please wait a moment and try again.';
    case 'server':
      return 'A server error occurred. Please try again later.';
    case 'unknown':
      return fallback || detail || 'An unexpected error occurred. Please try refreshing the page.';
  }
}

/**
 * Determine if an error is retryable based on its category.
 */
function isRetryable(category: ErrorCategory): boolean {
  return category === 'network' || category === 'rate_limit' || category === 'server';
}

/**
 * Main error handler that extracts structured error information.
 *
 * @param error - The error to handle (Error, API response, or unknown)
 * @param options - Configuration options
 * @returns Structured error information
 *
 * @example
 * ```typescript
 * try {
 *   await api.call();
 * } catch (err) {
 *   const handled = handleError(err, {
 *     fallback: 'Failed to fetch data',
 *     logContext: 'useMyHook:fetchData'
 *   });
 *   setError(handled.message);
 * }
 * ```
 */
export function handleError(error: unknown, options: HandleErrorOptions = {}): HandledError {
  const { fallback, logContext, log = true } = options;

  const statusCode = getStatusCode(error);
  const detail = getDetailMessage(error);
  const category = categorizeError(error, statusCode);
  const message = getFriendlyMessage(category, detail, fallback);
  const retryable = isRetryable(category);

  // Log error if requested
  if (log) {
    // Use the project's structured logger service instead of console.error
    logger.error(logContext ?? 'handleError', error);
  }

  return {
    message,
    category,
    originalError: error,
    statusCode,
    retryable,
  };
}

/**
 * Shorthand for extracting just the error message string.
 *
 * @param error - The error to handle
 * @param fallback - Fallback message if error cannot be categorized
 * @returns User-friendly error message
 *
 * @example
 * ```typescript
 * catch (err) {
 *   setError(getErrorMessage(err, 'Failed to save'));
 * }
 * ```
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  return handleError(error, { fallback, log: false }).message;
}

/** Map a scan-creation/scan-run failure to user-facing copy. Shared by
 * Applications.tsx (new-application discovery) and RescanModal.tsx. */
export function getFriendlyScanErrorMessage(err: unknown): string {
  const rawMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
  const message = getErrorMessage(err, 'An unexpected error occurred');
  const apiError = err instanceof ApiError ? err : null;
  const detail = apiError?.detail || rawMessage;
  const lowerDetail = detail.toLowerCase();

  if (apiError?.status === 401) {
    return 'Your session expired. Please log in again and retry the scan.';
  }
  if (apiError?.status === 429 || lowerDetail.includes('rate limit')) {
    return 'GitHub API rate limit exceeded. Please wait a few minutes or provide a GitHub token for higher limits.';
  }
  if (lowerDetail.includes('github api error: 401') || lowerDetail.includes('bad credentials')) {
    return 'GitHub authentication failed. Please verify your GitHub token and ensure it has repository access.';
  }
  if (lowerDetail.includes('github api error: 403') || lowerDetail.includes('forbidden')) {
    return 'GitHub access denied. Ensure the token has permission to read this repository.';
  }
  if (lowerDetail.includes('github api error: 404') || lowerDetail.includes('repository not found') || lowerDetail.includes('not found')) {
    return 'Repository not found. Please check the URL and ensure your token can access it.';
  }
  if (lowerDetail.includes('unsupported source type') && lowerDetail.includes('local')) {
    return 'Local source type is unsupported for this request. Use Browse upload or verify backend local scan configuration.';
  }
  if (lowerDetail.includes('local path') && lowerDetail.includes('does not exist')) {
    return 'Local path not found from backend. Enter a valid path reachable by the backend container and retry.';
  }
  if (lowerDetail.includes('currently supports') || lowerDetail.includes('no supported')) {
    // Unsupported language — pass backend message through as-is (already user-friendly)
    return detail;
  }
  if (lowerDetail.includes('no supported source files') || lowerDetail.includes('at least one file is required')) {
    return detail;
  }
  if (lowerDetail.includes('timed out') || lowerDetail.includes('timeout')) {
    return 'The AI analysis took too long to respond. This sometimes happens with larger repositories — please try again.';
  }
  if (lowerDetail.includes('network') || lowerDetail.includes('connection failed') || rawMessage === 'Failed to fetch') {
    return 'Request failed — the backend may be unreachable or the request payload may be too large. Ensure the backend is running and try again.';
  }
  if (lowerDetail.includes('fetch')) {
    // Generic fetch error — surface the real detail when available
    return detail !== rawMessage ? detail : 'Network error. Please check your connection and try again.';
  }

  // Preserve explicit backend/runtime failure details instead of collapsing
  // to the generic fallback string.
  if (message === 'An unexpected error occurred' && rawMessage !== 'An unexpected error occurred') {
    return rawMessage;
  }

  return message;
}

/**
 * Build a user-facing message from a failed scan payload.
 * Some failures only populate phase_message, so we must fall back to it.
 */
export function getFailedScanMessage(scan: Pick<AIBOMScanResponse, 'error_message' | 'phase_message'>): string {
  return scan.error_message || scan.phase_message || 'The scan could not complete. You can retry from the Applications list.';
}
